import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Edit2, Trash2 } from 'lucide-react';

import ConfirmationDialog from '../Shared/ConfirmationDialog';
import { deleteRoutingRuleService } from '../../utils/responsePartnerService';
import { removeRoutingRule, setError } from '../../reducers/responsePartnerReducer';
import { sortRulesByPriority } from '../../utils/responsePartnerUtils';

const RoutingRuleList = ({ rules = [], partners = [], loading, onEdit }) => {
  const dispatch = useDispatch();
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const partnersById = useMemo(() => {
    const map = new Map();
    for (const p of partners) map.set(p.id, p);
    return map;
  }, [partners]);

  const sortedRules = useMemo(() => sortRulesByPriority(rules), [rules]);

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;
    setIsDeleting(true);
    try {
      await deleteRoutingRuleService(pendingDeleteId);
      dispatch(removeRoutingRule(pendingDeleteId));
      setPendingDeleteId(null);
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to delete rule';
      dispatch(setError(message));
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading && rules.length === 0) {
    return <div className="text-stone-500 py-6 text-sm">Loading routing rules…</div>;
  }

  if (rules.length === 0) {
    return (
      <div className="border border-dashed border-stone-300 rounded-md p-6 text-center text-stone-500">
        No routing rules yet. Click <span className="font-semibold">Add Rule</span> to create one.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {sortedRules.map((rule) => {
        const partner = partnersById.get(rule.responsePartnerId);
        return (
          <div
            key={rule.id}
            className="border border-stone-200 rounded-md p-4 bg-white hover:border-stone-300"
          >
            <div className="flex flex-row items-start justify-between gap-3 flex-wrap">
              <div className="flex flex-col gap-1 flex-grow min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base font-semibold m-0 truncate">
                    {partner?.name || <span className="italic text-stone-500">Unknown partner</span>}
                  </h3>
                  <span className="text-xs text-stone-500 font-mono">
                    priority {rule.priorityOrder ?? 0}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-3xl bg-stone-100 text-stone-700">
                    scope: {rule.scope}
                  </span>
                  {rule.active ? (
                    <span className="text-xs px-2 py-0.5 rounded-3xl bg-green-100 text-green-800">
                      Active
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-3xl bg-stone-200 text-stone-700">
                      Inactive
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-xs text-stone-600 mt-1">
                  <div>
                    <span className="font-semibold">Issue types:</span>{' '}
                    {rule.issueTypes?.length > 0 ? (
                      <span className="inline-flex flex-wrap gap-1 align-middle">
                        {rule.issueTypes.map((issueType) => (
                          <span
                            key={issueType}
                            className="inline-block px-1.5 py-0.5 rounded bg-stone-100 text-stone-700 font-mono text-[11px]"
                          >
                            {issueType}
                          </span>
                        ))}
                      </span>
                    ) : (
                      'any'
                    )}
                  </div>
                  <div>
                    <span className="font-semibold">Min priority:</span>{' '}
                    {rule.minPriority || 'any'}
                  </div>
                  {rule.scope === 'user' && (
                    <div>
                      <span className="font-semibold">Scope user id:</span>{' '}
                      <span className="font-mono">{rule.scopeUserId || '—'}</span>
                    </div>
                  )}
                  {rule.scope === 'property' && (
                    <div>
                      <span className="font-semibold">Property key:</span>{' '}
                      <span className="font-mono">{rule.scopePropertyKey || '—'}</span>
                    </div>
                  )}
                  {rule.conditions && Object.keys(rule.conditions).length > 0 && (
                    <div className="md:col-span-2">
                      <span className="font-semibold">Conditions:</span>{' '}
                      <code className="text-xs">
                        {(() => {
                          try {
                            return JSON.stringify(rule.conditions);
                          } catch {
                            return '{…}';
                          }
                        })()}
                      </code>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-row items-center gap-1 shrink-0">
                <button
                  onClick={() => onEdit?.(rule)}
                  data-amp-track-section="Routing Rule List"
                  data-amp-track-action="Edit_Rule_Click"
                  data-amp-track-description="Edit Routing Rule"
                  className="flex items-center gap-1 px-2 py-1 rounded-md bg-transparent text-stone-700 hover:bg-stone-200 text-sm"
                >
                  <Edit2 size={16} /> Edit
                </button>
                <button
                  onClick={() => setPendingDeleteId(rule.id)}
                  data-amp-track-section="Routing Rule List"
                  data-amp-track-action="Delete_Rule_Click"
                  data-amp-track-description="Delete Routing Rule"
                  className="flex items-center gap-1 px-2 py-1 rounded-md bg-transparent text-red-700 hover:bg-red-50 text-sm"
                >
                  <Trash2 size={16} /> Delete
                </button>
              </div>
            </div>
          </div>
        );
      })}

      <ConfirmationDialog
        isOpen={Boolean(pendingDeleteId)}
        onClose={() => (isDeleting ? null : setPendingDeleteId(null))}
        onConfirm={handleConfirmDelete}
        ampSection="Routing Rule List"
        message={
          <div className="text-sm text-stone-700">
            <p className="font-semibold m-0">Delete this routing rule?</p>
            <p className="text-xs text-stone-500 mt-1 mb-0">This cannot be undone.</p>
          </div>
        }
      />
    </div>
  );
};

export default RoutingRuleList;
